import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaArrowRight } from 'react-icons/fa';
import { useSettings } from '../../context/SettingsContext';
import { getImageUrl } from '../../utils/helpers';

const PopupNotice = () => {
    const [show, setShow] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const { settings } = useSettings();
    const popup = settings?.popup || {};

    useEffect(() => {
        if (!popup.isActive) return;

        if (popup.showOnce) {
            const shown = sessionStorage.getItem('popupShown');
            if (shown) return;
        }

        const timer = setTimeout(() => {
            setShow(true);
            if (popup.showOnce) sessionStorage.setItem('popupShown', 'true');
        }, popup.delay || 3000);

        return () => clearTimeout(timer);
    }, [popup]);

    if (!popup.isActive || !show) return null;

    const popupImage = popup.image ? getImageUrl(popup.image) : null;

    return (
        <AnimatePresence mode="wait">
            {show && (
                <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4, ease: 'easeOut' }}
                        onClick={() => setShow(false)}
                        className="absolute inset-0 bg-black/50 backdrop-blur-md"
                    />

                    {/* Popup Container */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.85, y: 40 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 30 }}
                        transition={{
                            type: 'spring',
                            damping: 30,
                            stiffness: 350,
                            mass: 0.8,
                        }}
                        className="relative w-full max-w-lg z-[91]"
                    >
                        <div className="bg-white rounded-[2rem] shadow-[0_25px_60px_-15px_rgba(0,0,0,0.3)] overflow-hidden relative">

                            {/* Decorative Background Blobs */}
                            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-primary-100/40 to-secondary-100/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary-100/30 to-primary-100/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

                            {/* Close Button */}
                            <motion.button
                                initial={{ opacity: 0, rotate: -90 }}
                                animate={{ opacity: 1, rotate: 0 }}
                                transition={{ delay: 0.3, duration: 0.3 }}
                                onClick={() => setShow(false)}
                                className="absolute top-4 right-4 w-9 h-9 bg-white/80 backdrop-blur-sm hover:bg-red-50 rounded-full flex items-center justify-center text-gray-400 hover:text-red-500 transition-all duration-300 z-20 shadow-sm hover:shadow-md hover:scale-110 border border-gray-100"
                            >
                                <FaTimes className="w-3.5 h-3.5" />
                            </motion.button>

                            {/* Image Section */}
                            {popupImage && (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ delay: 0.15, duration: 0.5 }}
                                    className="relative h-52 overflow-hidden"
                                >
                                    {/* Shimmer placeholder */}
                                    {!imageLoaded && (
                                        <div className="absolute inset-0 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />
                                    )}
                                    <motion.img
                                        src={popupImage}
                                        alt=""
                                        className="w-full h-full object-cover"
                                        initial={{ scale: 1.1 }}
                                        animate={{ scale: 1 }}
                                        transition={{ duration: 0.8, ease: 'easeOut' }}
                                        onLoad={() => setImageLoaded(true)}
                                    />
                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent"></div>
                                </motion.div>
                            )}

                            {/* Content Section */}
                            <div className={`relative z-10 px-8 ${popupImage ? 'pt-2 pb-8' : 'py-10'} text-center`}>
                                {/* Decorative dot */}
                                {!popupImage && (
                                    <motion.div
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                        transition={{ delay: 0.2, type: 'spring', stiffness: 300 }}
                                        className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg shadow-primary-500/20"
                                    >
                                        <span className="text-2xl">📢</span>
                                    </motion.div>
                                )}

                                {/* Title */}
                                {popup.title && (
                                    <motion.h2
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2, duration: 0.4, ease: 'easeOut' }}
                                        className="text-2xl md:text-3xl font-extrabold text-gray-900 mb-3 leading-tight"
                                    >
                                        {popup.title}
                                    </motion.h2>
                                )}

                                {/* Content */}
                                {popup.content && (
                                    <motion.p
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.3, duration: 0.4, ease: 'easeOut' }}
                                        className="text-gray-500 mb-8 leading-relaxed max-w-sm mx-auto"
                                    >
                                        {popup.content}
                                    </motion.p>
                                )}

                                {/* Button */}
                                {popup.buttonText && popup.buttonLink && (
                                    <motion.div
                                        initial={{ opacity: 0, y: 15 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.4, duration: 0.4, ease: 'easeOut' }}
                                    >
                                        <Link
                                            to={popup.buttonLink}
                                            onClick={() => setShow(false)}
                                            className="group inline-flex items-center px-8 py-3.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white font-bold rounded-2xl shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
                                        >
                                            {popup.buttonText}
                                            <FaArrowRight className="ml-2.5 w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                                        </Link>

                                        {/* Dismiss text */}
                                        <motion.button
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ delay: 0.6 }}
                                            onClick={() => setShow(false)}
                                            className="block mx-auto mt-4 text-sm text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                        >
                                            No thanks, dismiss
                                        </motion.button>
                                    </motion.div>
                                )}

                                {/* If no button, show close text */}
                                {(!popup.buttonText || !popup.buttonLink) && (
                                    <motion.button
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        transition={{ delay: 0.4 }}
                                        onClick={() => setShow(false)}
                                        className="mt-4 px-6 py-2.5 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all duration-200 font-medium"
                                    >
                                        Got it, close
                                    </motion.button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default PopupNotice;